import { Link } from "react-router-dom";
import { PublicLayout } from "../../components/public-layout.js";
import {
  HeroSection,
  FeatureCard,
  CTASection,
} from "../../components/marketing-components.js";

export function DocumentAccessibilityPage() {
  return (
    <PublicLayout>
      <HeroSection
        title="Document accessibility scanning"
        subtitle="Detect and analyze PDFs and documents separately from your website"
      />

      <section className="marketing-section">
        <div className="section-content">
          <h2>Why documents need separate testing</h2>

          <p>
            PDF and document accessibility is different from web accessibility.
            We detect documents on your site and scan them using PDF-specific
            standards.
          </p>

          <div className="features-grid features-grid--three">
            <FeatureCard
              title="Auto-detection"
              description="We find downloadable PDFs, Word docs, and spreadsheets on your site."
            />
            <FeatureCard
              title="PDF analysis"
              description="Text extraction, tagging, form fields, alternative text for charts/images."
            />
            <FeatureCard
              title="Separate scoring"
              description="Documents get their own accessibility score and remediation statement."
            />
          </div>
        </div>
      </section>

      <section className="marketing-section marketing-section--alt">
        <div className="section-content">
          <h2>What we check in documents</h2>

          <div className="doc-checklist">
            <div className="check-item">
              <h4>Text and images</h4>
              <p>
                All images have alternative text. Charts and graphs have
                descriptions. No text embedded as image.
              </p>
            </div>

            <div className="check-item">
              <h4>Document structure</h4>
              <p>
                Headings, lists, and tables are tagged properly. Reading order
                is logical.
              </p>
            </div>

            <div className="check-item">
              <h4>Forms and interactivity</h4>
              <p>
                Form fields are labeled. Action buttons are accessible. No
                required fields lack labels.
              </p>
            </div>

            <div className="check-item">
              <h4>Color and contrast</h4>
              <p>
                Text has sufficient contrast. Color alone is not used to convey
                information.
              </p>
            </div>

            <div className="check-item">
              <h4>Fonts and readability</h4>
              <p>
                Font sizes are readable. Text can be resized. Sans-serif is
                preferred for readability.
              </p>
            </div>

            <div className="check-item">
              <h4>Metadata</h4>
              <p>
                Document title is set. Language is declared. Embedded fonts are
                accessible.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="marketing-section">
        <div className="section-content">
          <h2>Limitations and when you need help</h2>

          <p>
            Automated document scanning finds structure and content issues. Some
            things need human review:
          </p>

          <ul className="limitation-list">
            <li>
              <strong>Scanned images (image-only PDFs):</strong> Some PDFs are
              just pictures of pages. These need manual OCR and tagging.
            </li>
            <li>
              <strong>Complex layouts:</strong> Multi-column documents with
              sidebars may have reading order issues that automation can't fully
              validate.
            </li>
            <li>
              <strong>Non-Latin scripts:</strong> Chinese, Arabic, Hebrew, and
              other scripts may need specialized review.
            </li>
            <li>
              <strong>Interactive PDFs:</strong> Complex forms or embedded media
              need manual testing.
            </li>
            <li>
              <strong>Design quality:</strong> Whether a document "feels"
              accessible to real users is a human judgment.
            </li>
          </ul>

          <p style={{ marginTop: "24px" }}>
            <Link to="/manual-review">
              Learn about professional document review →
            </Link>
          </p>
        </div>
      </section>

      <section className="marketing-section marketing-section--alt">
        <div className="section-content">
          <h2>Document remediation tips</h2>

          <div className="tip-box">
            <h3>Quick wins</h3>
            <ul>
              <li>Add descriptive titles to all PDFs</li>
              <li>Use "Save as PDF" from Office instead of print-to-PDF</li>
              <li>Tag headings, lists, and tables properly before exporting</li>
              <li>Add alt text to images in source document</li>
              <li>Use logical tab order for forms</li>
            </ul>
          </div>

          <div className="tip-box">
            <h3>Tools that help</h3>
            <ul>
              <li>
                <strong>Microsoft Office:</strong> Built-in accessibility
                checker and PDF export
              </li>
              <li>
                <strong>Adobe Acrobat Pro:</strong> Full tagging and remediation
                suite
              </li>
              <li>
                <strong>PAC (PDF Accessibility Checker):</strong> Free automated
                testing tool
              </li>
            </ul>
          </div>
        </div>
      </section>

      <CTASection
        title="Start scanning documents"
        description="Automatic document detection and accessibility analysis starting with Growth plan."
        primaryCTA={{ label: "View plans", href: "/plans" }}
      />
    </PublicLayout>
  );
}
